import { z } from "zod";
import { AddressSchema } from "./companies";
import { CustomFieldValuesSchema } from "./customFields";
import { CurrencyCodeSchema, ExchangeRateSnapshotSchema } from "./currency";
import { DateOnlyStringSchema } from "./dates";
import { DimensionUnitSchema, ItemUnitSchema, WeightUnitSchema } from "./items";
import { PricingUnitSchema } from "./priceLists";
import { QuoteDocumentSchema } from "./quoteDocuments";
import {
  QuoteDiscountSchema,
  QuoteOrganizationSchema,
  QuoteResponseSchema,
  QuoteStatusSchema,
  QuoteTemplateIdSchema,
} from "./quotes";

/**
 * The customer-safe view of a quote returned by the public quote link.
 *
 * This is an allowlist, and that is the point. The public endpoint is
 * unauthenticated by design, so anything named here is published to whoever
 * holds the link. A field that is not named here is invisible — including
 * fields added to the internal quote model later, which is the failure mode
 * this schema exists to prevent. Add a field only when the public page renders
 * it.
 *
 * Never add: cost, margin, yield, price-model inputs or calculations, item
 * operations or components, price list snapshots, `itemsDeepCopy`, agent
 * reasoning or confidence, external/ERP mappings, or internal user ids.
 */

/** An asset as shown on the public quote: enough to render an item image. */
export const PublicQuoteAssetSchema = z.object({
  fileName: z.string(),
  fileType: z.string().optional(),
  externalUrl: z.string(),
  webViewFileName: z.string().nullable().optional(),
  webViewFileType: z.string().nullable().optional(),
  webViewExternalUrl: z.string().nullable().optional(),
});
export type PublicQuoteAsset = z.infer<typeof PublicQuoteAssetSchema>;

/**
 * An item as shown on the public quote.
 *
 * Deliberately eleven fields. The internal `HydratedItem` carries the entire
 * costing model — `costBreaks`, `margin`, `yield`, `pricingLines`,
 * `priceModelInputs`, `operations` and recursive `components`, each with their
 * own `reasoning` — none of which a customer may see.
 */
export const PublicQuoteItemSchema = z.object({
  name: z.string(),
  revision: z.string(),
  description: z.string().nullable(),
  unit: ItemUnitSchema,
  weight: z.string().nullable(),
  weightUnit: WeightUnitSchema.nullable(),
  length: z.string().nullable(),
  width: z.string().nullable(),
  height: z.string().nullable(),
  dimensionUnit: DimensionUnitSchema.nullable(),
  primaryFileAsset: PublicQuoteAssetSchema.nullable(),
});
export type PublicQuoteItem = z.infer<typeof PublicQuoteItemSchema>;

/**
 * A quantity break as shown on the public quote: the prices and totals the
 * templates render, without how the price was derived (`unitPriceSource`,
 * `priceListLineId`, `conversionFactor`, `convertedFromUnitPrice`).
 */
export const PublicQuoteQuantityBreakSchema = z.object({
  quantity: z.string(),
  unitPrice: z.string().nullable(),
  unitPriceOverride: z.string().nullable(),
  amount: z.string().nullable(),
  convertedUnitPrice: z.string().nullable().optional(),
  convertedAmount: z.string().nullable().optional(),
  totalWeight: z.string().nullable(),
  totalWeightUnit: WeightUnitSchema.nullable().optional(),
  pricingUnit: PricingUnitSchema.nullable().optional(),
});
export type PublicQuoteQuantityBreak = z.infer<
  typeof PublicQuoteQuantityBreakSchema
>;

export const PublicQuoteItemLineSchema = z.object({
  id: z.string(),
  type: z.literal("ITEM"),
  description: z.string(),
  amount: z.string().nullable(),
  quantityBreaks: z.array(PublicQuoteQuantityBreakSchema),
  item: PublicQuoteItemSchema.nullable(),
  unit: ItemUnitSchema.nullable(),
  customFields: CustomFieldValuesSchema.optional(),
});
export type PublicQuoteItemLine = z.infer<typeof PublicQuoteItemLineSchema>;

export const PublicQuoteSectionLineSchema = z.object({
  id: z.string(),
  type: z.literal("SECTION"),
  description: z.string(),
});
export type PublicQuoteSectionLine = z.infer<
  typeof PublicQuoteSectionLineSchema
>;

export const PublicQuoteNoteLineSchema = z.object({
  id: z.string(),
  type: z.literal("NOTE"),
  description: z.string(),
});
export type PublicQuoteNoteLine = z.infer<typeof PublicQuoteNoteLineSchema>;

/**
 * A discount line as shown on the public quote: the discount config plus the
 * server-computed row amounts (`amount` in base currency, `convertedAmount` in
 * the presentation currency), so templates render exactly what the totals
 * subtract. Both are null when the subtotal cannot be computed.
 */
export const PublicQuoteDiscountLineSchema = z.object({
  id: z.string(),
  type: z.literal("DISCOUNT"),
  description: z.string(),
  discount: QuoteDiscountSchema,
  amount: z.string().nullable(),
  convertedAmount: z.string().nullable(),
});
export type PublicQuoteDiscountLine = z.infer<
  typeof PublicQuoteDiscountLineSchema
>;

/** A quote line as shown on the public quote. No line carries `reasoning`. */
export const PublicQuoteLineSchema = z.discriminatedUnion("type", [
  PublicQuoteItemLineSchema,
  PublicQuoteSectionLineSchema,
  PublicQuoteNoteLineSchema,
  PublicQuoteDiscountLineSchema,
]);
export type PublicQuoteLine = z.infer<typeof PublicQuoteLineSchema>;

/** The signer's name and time. Their email and IP address are not published. */
export const PublicQuoteSignatureSchema = z.object({
  name: z.string(),
  signedAt: z.coerce.date(),
});

/** The quote's location, as shown on public commercial terms. */
export const PublicQuoteLocationSchema = z.object({
  name: z.string(),
  address: AddressSchema.nullable(),
  logoUrl: z.string().nullable().optional(),
});

/** The customer's own company details, without ERP mappings or internal ids. */
export const PublicQuoteCompanySchema = z.object({
  name: z.string(),
  address: AddressSchema.nullable(),
  phone: z.string().nullable(),
  email: z.string().nullable(),
});

/** A contact on the quote: name and email only, never ids. */
export const PublicQuotePersonSchema = z.object({
  firstName: z.string(),
  lastName: z.string(),
  email: z.string().nullable(),
});

/** Only the opportunity's name is published; its value and stage are not. */
export const PublicQuoteOpportunitySchema = z.object({
  name: z.string(),
});

/** The words a quote page can carry as a diagonal watermark. */
export const QUOTE_WATERMARK = { TEST: "TEST", DRAFT: "DRAFT" } as const;
/** Validates a quote page watermark word. */
export const QuoteWatermarkSchema = z.enum([
  QUOTE_WATERMARK.TEST,
  QUOTE_WATERMARK.DRAFT,
]);
/** A quote page watermark word. */
export type QuoteWatermark = z.infer<typeof QuoteWatermarkSchema>;

export const PublicQuoteSchema = z.object({
  id: z.string(),
  status: QuoteStatusSchema,
  watermark: QuoteWatermarkSchema.nullable(),
  quoteDisplayNumber: z.string(),
  revision: z.number(),
  isLatestRevision: z.boolean(),
  latestRevisionId: z.string().nullable(),
  name: z.string().nullable(),
  deliveryTerms: z.string().nullable(),
  paymentTerms: z.string().nullable(),
  termsOfService: z.string().nullable(),
  quoteFooter: z.string().nullable(),
  coverLetterMarkdown: z.string().nullable(),
  enableCoverLetter: z.boolean(),
  expirationDate: DateOnlyStringSchema.nullable(),
  quoteDate: DateOnlyStringSchema.nullable(),
  createdAt: z.coerce.date(),
  requiresCustomerSignature: z.boolean(),
  showTotals: z.boolean(),
  showTotalWeight: z.boolean(),
  subtotal: z.string().nullable(),
  subtotalWithDiscounts: z.string().nullable(),
  total: z.string().nullable(),
  /** Display decimals for prices, from the org's price-rounding config; null when rounding is off. */
  priceDecimals: z.number().nullable(),
  isSigned: z.boolean(),
  signature: PublicQuoteSignatureSchema.nullable(),
  publicQuoteTemplate: QuoteTemplateIdSchema,
  publicAccessRevokedAt: z.coerce.date().nullable(),
  customFields: CustomFieldValuesSchema.nullable(),
  quoteCurrency: CurrencyCodeSchema.nullable(),
  baseCurrency: CurrencyCodeSchema.nullable(),
  snapshot: ExchangeRateSnapshotSchema.nullable(),
  salesRepName: z.string().nullable(),
  salesRepEmail: z.string().nullable(),
  estimatorNames: z.array(z.string()),
  lines: z.array(PublicQuoteLineSchema),
  organization: QuoteOrganizationSchema.nullable(),
  location: PublicQuoteLocationSchema.nullable(),
  customerCompany: PublicQuoteCompanySchema.nullable(),
  people: z.array(PublicQuotePersonSchema),
  shippingAddress: AddressSchema.nullable(),
  opportunity: PublicQuoteOpportunitySchema.nullable(),
  /**
   * The validated (sanitized) HTML document to render this quote, present only
   * when the owning org has the `quote-documents` flag on and the quote has a
   * stored, render-valid document. Absent means the legacy React template path
   * renders instead, so flag-off behavior is byte-identical to before.
   */
  quoteDocument: QuoteDocumentSchema.optional(),
  /**
   * Signed, org-scoped asset URLs keyed by the document's `asset-id` values,
   * accompanying {@link PublicQuoteSchema.quoteDocument}. The renderer reads
   * from this map and never signs or fetches URLs itself.
   */
  assetUrls: z.record(z.string(), z.string()).optional(),
});
export type PublicQuote = z.infer<typeof PublicQuoteSchema>;

/** The response body of the public quote endpoints. */
export const PublicQuoteResponseSchema = z.object({
  quote: PublicQuoteSchema,
});
export type PublicQuoteResponse = z.infer<typeof PublicQuoteResponseSchema>;

/** The stored PDF asset generated for a public quote. */
export const PublicQuotePdfResponseSchema = z.object({
  assetId: z.string(),
  assetUrl: z.string().url(),
});
export type PublicQuotePdfResponse = z.infer<
  typeof PublicQuotePdfResponseSchema
>;

/**
 * The response body of the internal `GET /quotes/:id` endpoint. `publicQuote`
 * is the customer-safe projection the editor preview renders the stored
 * document from; it rides alongside the quote so the internal preview shows
 * exactly what the public page shows. It is present when the quote has a
 * document that renders, or when the caller asks for it with
 * `?includePublicQuote=true` (how the template editor borrows a real quote's
 * data as preview sample data).
 */
export const GetQuoteResponseSchema = z.object({
  quote: QuoteResponseSchema,
  publicQuote: PublicQuoteSchema.optional(),
});
export type GetQuoteResponse = z.infer<typeof GetQuoteResponseSchema>;

/**
 * The response body of the internal `PUT /quotes/:id` endpoint. Mirrors
 * {@link GetQuoteResponseSchema}: `publicQuote` is the recomputed customer-safe
 * projection, present only when the saved quote has a document that renders, so
 * the editor preview refreshes to match the public page after a save.
 */
export const UpdateQuoteResponseSchema = z.object({
  quote: QuoteResponseSchema,
  publicQuote: PublicQuoteSchema.optional(),
});
export type UpdateQuoteResponse = z.infer<typeof UpdateQuoteResponseSchema>;
